import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {HTMLElement | null} */
  #scroller = null;

  // Ref-dependent setup lives in refsConnected() (not connectedCallback) so it
  // still runs when the element is upgraded before its <dialog>/media subtree is
  // parsed — e.g. in Facebook/Instagram in-app browsers that disturb deferred
  // module ordering. The base class calls this once all requiredRefs are present.
  refsConnected() {
    this.refs.dialog.addEventListener('scroll', this.handleScroll);

    // On desktop the dialog itself scrolls (vertical); on mobile the images
    // are a horizontal scroll-snap carousel. Sync the active thumbnail from the
    // carousel's scroll position synchronously (no debounce / IntersectionObserver)
    // so the highlight tracks the swipe instantly instead of lagging behind.
    this.#scroller = this.querySelector('.dialog-zoomed-gallery');
    this.#scroller?.addEventListener('scroll', this.#handleGalleryScroll, { passive: true });

    // Lock the page behind the maximized image. The <dialog scroll-lock>
    // attribute is inert here (dialog.js only wires scroll-lock for <details>),
    // so release the html[scroll-lock] lock ourselves on every close path.
    this.refs.dialog.addEventListener('close', this.#releaseScrollLock);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Optional chaining: if the element was disconnected before refsConnected()
    // ever fired (refs never resolved), this.refs.dialog is undefined — don't
    // throw a TypeError tearing down a component that never finished setup.
    this.refs.dialog?.removeEventListener('scroll', this.handleScroll);
    this.#scroller?.removeEventListener('scroll', this.#handleGalleryScroll);
    this.refs.dialog?.removeEventListener('close', this.#releaseScrollLock);
    this.#releaseScrollLock();
  }

  #releaseScrollLock = () => {
    document.documentElement.removeAttribute('scroll-lock');
  };

  /** @type {number} */
  #activeIndex = 0;

  /**
   * Instantly sync the active thumbnail to the mobile carousel's scroll
   * position. Runs synchronously on every scroll tick (gated on index change),
   * unlike the debounced desktop `handleScroll`, so the highlight never lags.
   */
  #handleGalleryScroll = () => {
    // Only track the carousel while the dialog is actually open — never during
    // open()'s scrollIntoView-driven scroll or close/re-render teardown.
    const scroller = this.#scroller;
    const { media, dialog } = this.refs;
    if (!scroller || !media.length || !dialog.open) return;

    const slideWidth = scroller.clientWidth || 1;
    const index = Math.max(0, Math.min(media.length - 1, Math.round(scroller.scrollLeft / slideWidth)));
    if (index === this.#activeIndex) return;

    const activeMedia = media[index];
    if (!activeMedia) return;

    this.#activeIndex = index;
    this.#setActiveThumbnail(index);
    this.loadHighResolutionImage(activeMedia);
    // NB: deliberately does NOT dispatch ZoomMediaSelectedEvent. That drives
    // media-gallery's slideshow.select() on the main gallery, and firing it from
    // the mobile carousel (incl. during open()) raced with the open/close view
    // transitions and the variant-swap gallery re-render, breaking re-open.
  };

  /**
   * Mark the thumbnail at `index` as selected and the rest as not.
   * @param {number} index
   */
  #setActiveThumbnail(index) {
    Array.from(this.refs.thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      // showModal() is missing on very old WebViews and can throw (e.g. the
      // dialog is already open, or a detached/partial <dialog> implementation).
      // Fall back to the non-modal open path so the lightbox still appears; the
      // `[open]` attribute plus the position:fixed/inset:0 rule in base.css keeps
      // it covering the viewport even without native top-layer promotion.
      try {
        dialog.showModal();
      } catch (error) {
        if (!dialog.open) dialog.setAttribute('open', '');
      }
      // Prevent the page behind the fullscreen image from scrolling.
      document.documentElement.setAttribute('scroll-lock', '');

      // Highlight the opened image's thumbnail up front so it's correct before
      // any scroll events fire.
      this.#activeIndex = index;
      this.#setActiveThumbnail(index);

      // Defer the scroll one frame. On mobile the images are a `scroll-snap-type:
      // x mandatory` flex carousel; scrolling to the target slide synchronously
      // inside showModal() (before the container is laid out) can leave WebKit's
      // mandatory snap un-established, which renders the slides stacked instead of
      // as a swipeable carousel. A rAF lets layout settle first.
      requestAnimationFrame(() => {
        for (const target of [targetThumbnail, targetImage]) {
          target?.scrollIntoView({ behavior: 'instant' });
        }
      });
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#activeIndex = index;
    this.#setActiveThumbnail(index);

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
